import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  api,
  type ParameterTimeSeriesConfiguration,
  type ParametersLocation,
  type ParametersObservedMapLayer,
  type ParametersObservedTimeSeriesForLocationResult,
} from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { resolveFetchUnitForParameter } from "@/lib/parameters/parameter-units.utils";
import AsyncTaskQueue from "@/lib/queue/async-queue";

export type LocationParameterTimeSeriesResult =
  ParametersObservedTimeSeriesForLocationResult;

export type LocationParameterSeriesRequest = {
  locationId: string;
  parameterId: string;
  unitCode: string;
};

export const keys = {
  observedParameters: () => ["parameters", "observed", "parameters"] as const,
  observedMapLayers: () => ["parameters", "observed", "layers"] as const,
  locations: () => ["parameters", "locations"] as const,
  locationParameterSeriesPrefix: (locationId: string) =>
    ["parameters", "locations", locationId, "parameters", "timeseries"] as const,
  locationParameterSeries: (locationId: string, parameterId: string, unitCode: string) =>
    [...keys.locationParameterSeriesPrefix(locationId), parameterId, unitCode] as const,
};

/** TanStack query options for one observed location+parameter series (markers + panel share keys). */
export function observedLocationParameterDataQuery(
  locationId: string,
  parameterId: string,
  unitCode: string,
) {
  return queryOptions({
    queryKey: keys.locationParameterSeries(locationId, parameterId, unitCode),
    queryFn: ({ signal }) =>
      fetchLocationParameterTimeSeries(locationId, parameterId, unitCode, signal),
    retry: false as const,
  });
}

/** Parameter ids from widget time-series config that this location supports. */
export function requiredParameterIdsForLocation(
  location: ParametersLocation,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): string[] {
  const configuredIds = new Set(
    timeSeriesConfigurations.flatMap((c) => c.series.map((s) => s.parameter_id)),
  );
  return location.parameter_ids.filter((id) => configuredIds.has(id));
}

/** Union of all parameter ids referenced in time-series groups. */
export function configuredTimeSeriesParameterIds(
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): string[] {
  return [
    ...new Set(
      timeSeriesConfigurations.flatMap((c) => c.series.map((s) => s.parameter_id)),
    ),
  ];
}

function requestKey(request: LocationParameterSeriesRequest): string {
  return `${request.locationId}:${request.parameterId}:${request.unitCode}`;
}

/** Unique (locationId, parameterId, unitCode) pairs for the given parameter id list. */
export function buildLocationParameterSeriesRequestsForParameterIds(
  locations: ParametersLocation[],
  parameterIds: string[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): LocationParameterSeriesRequest[] {
  const idSet = new Set(parameterIds);
  if (idSet.size === 0) return [];

  const seen = new Set<string>();
  const requests: LocationParameterSeriesRequest[] = [];

  for (const location of locations) {
    for (const parameterId of location.parameter_ids) {
      if (!idSet.has(parameterId)) continue;
      const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
      if (!unitCode) continue;
      const request = { locationId: location.id, parameterId, unitCode };
      const dedupeKey = requestKey(request);
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      requests.push(request);
    }
  }

  return requests;
}

/** Marker layer fetch set: active display-group parameters only (charts load on panel open). */
export function buildStationMarkerLayerSeriesRequests(
  locations: ParametersLocation[],
  markerParameterIds: string[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): LocationParameterSeriesRequest[] {
  return buildLocationParameterSeriesRequestsForParameterIds(
    locations,
    markerParameterIds,
    timeSeriesConfigurations,
  );
}

/** Parameter ids from the first CMS time-series group (marker display order). */
export function firstGroupParameterIds(
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): string[] {
  const firstGroup = timeSeriesConfigurations[0];
  if (!firstGroup) return [];
  return firstGroup.series.map((s) => s.parameter_id);
}

/** Marker batch: first group only, supported (locationId, parameterId) pairs. */
export function buildMarkerParameterSeriesRequests(
  locations: ParametersLocation[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): LocationParameterSeriesRequest[] {
  const parameterIds = new Set(firstGroupParameterIds(timeSeriesConfigurations));
  if (parameterIds.size === 0) return [];

  const seen = new Set<string>();
  const requests: LocationParameterSeriesRequest[] = [];

  for (const location of locations) {
    for (const parameterId of location.parameter_ids) {
      if (!parameterIds.has(parameterId)) continue;
      const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
      if (!unitCode) continue;
      const request = { locationId: location.id, parameterId, unitCode };
      const dedupeKey = requestKey(request);
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      requests.push(request);
    }
  }

  return requests;
}

/** Unique (locationId, parameterId, unitCode) pairs for all visible stations and config groups. */
export function buildLocationParameterSeriesRequests(
  locations: ParametersLocation[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): LocationParameterSeriesRequest[] {
  const seen = new Set<string>();
  const requests: LocationParameterSeriesRequest[] = [];

  for (const location of locations) {
    for (const parameterId of requiredParameterIdsForLocation(
      location,
      timeSeriesConfigurations,
    )) {
      const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
      if (!unitCode) continue;
      const request = { locationId: location.id, parameterId, unitCode };
      const dedupeKey = requestKey(request);
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      requests.push(request);
    }
  }

  return requests;
}

export function buildChartParameterSeriesRequestsForLocation(
  location: ParametersLocation,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
  chartParameterIds: string[],
): LocationParameterSeriesRequest[] {
  const allowed = new Set(chartParameterIds);
  const seen = new Set<string>();
  const requests: LocationParameterSeriesRequest[] = [];

  for (const parameterId of requiredParameterIdsForLocation(
    location,
    timeSeriesConfigurations,
  )) {
    if (!allowed.has(parameterId)) continue;
    const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
    if (!unitCode) continue;
    const request = { locationId: location.id, parameterId, unitCode };
    const dedupeKey = requestKey(request);
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    requests.push(request);
  }

  return requests;
}

export function useObservedParametersQuery(enabled = true) {
  return useQuery({
    queryKey: keys.observedParameters(),
    queryFn: () => api.parameters.listObservedParameters(),
    select: (response) => response.data,
    enabled,
  });
}

export function useObservedParametersForConfigurations(
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
  enabled: boolean,
) {
  const paramIds = useMemo(
    () => configuredTimeSeriesParameterIds(timeSeriesConfigurations),
    [timeSeriesConfigurations],
  );

  const query = useObservedParametersQuery(enabled && paramIds.length > 0);

  const data = useMemo(
    () => query.data?.filter((p) => paramIds.includes(p.id)),
    [query.data, paramIds],
  );

  return { ...query, data };
}

export function useObservedMapLayersQuery() {
  return useQuery({
    queryKey: keys.observedMapLayers(),
    queryFn: (): Promise<ParametersObservedMapLayer[]> =>
      unwrapAxiosData(api.parameters.listObservedMapLayers()),
  });
}

export function useObservedLocationsQuery(enabled = true) {
  return useQuery({
    queryKey: keys.locations(),
    queryFn: () => api.parameters.listLocations(),
    select: (response) => response.data,
    enabled,
  });
}

const observedLocationTimeSeriesQueue =
  new AsyncTaskQueue<LocationParameterTimeSeriesResult>({
    concurrencyLimit: 32,
  });

export async function fetchLocationParameterTimeSeries(
  locationId: string,
  parameterId: string,
  unitCode: string,
  signal?: AbortSignal,
): Promise<LocationParameterTimeSeriesResult> {
  const { promise } = observedLocationTimeSeriesQueue.enqueue({
    signal,
    run: async ({ signal }) => {
      signal?.throwIfAborted();
      const response = await api.parameters.getObservedParameterTimeSeriesForLocation(
        parameterId,
        locationId,
        unitCode,
        { signal },
      );
      return response.data;
    },
  });
  return await promise;
}

export function useObservedLocationParameterSeriesQueries(
  requests: LocationParameterSeriesRequest[],
) {
  return useQueries({
    queries: requests.map(({ locationId, parameterId, unitCode }) =>
      observedLocationParameterDataQuery(locationId, parameterId, unitCode),
    ),
  });
}
